import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  MenuItem,
  Popover,
  Select,
  Typography,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import SettingsReport from "./SettingsReport";
import StrategyTaxStateSetting from "./StrategyTaxStateSetting";
import StrategyFederalTaxSetting from "./StrategyFederalTaxSetting";
import {
  PerformanceReportTypeEnum,
  ReportValueCardProps,
  PDFReportRequest,
  PDFReportClientInfoItem,
  PDFReportThemeInfoItem,
  ReportChartDataPoint,
  ReportPdfResponse,
  OrionTransaction,
  BucketPairReportStatus,
  BucketReportStatusEnum,
  BucketTotals,
  BucketThemedCards,
  KeepThemedCardValues,
  ProtectThemedCardValues,
  ReportThemedCardConfig,
  PDFReportValueType,
  PDFReportChartType,
} from "helpers/types";
import ChartsReport from "./Charts/ChartsReport";
import {
  BucketSummaryEnum,
  ReportJobStatus,
  SingleValueCardColor,
  SingleValueCardType,
} from "constants/index";
import {
  StrategyReportSingleValueCardLabels,
  StrategyReportSingleValueCardDescriptions,
  StrategyReportThemedCardLabels,
  StrategyReportThemedCardDescriptions,
  StrategyReportThemedCardValuesLabels,
  StrategyReportThemedCardBlurbs,
  KEEP_TOTAL_OPTION,
} from "helpers/constants";
import { toPdfValues } from "./reportPdfUtils";
import SingleValueCard from "./SingleValueCard";
import MultiValuesCard from "./MultiValuesCard";
import CurrentAllocationChartReport from "./Charts/CurrentAllocationChartReport";
import { DrawdownComparisonChart } from "./Charts/DrawdownComparisonChart";
import { useStrategyReportValue } from "hooks/useReportStorage";
import { useFederalTaxRate } from "hooks/useFederalTaxRate";
import { useStateTaxRate } from "hooks/useStateTaxRate";
import { useMemo, useState, useCallback, useEffect } from "react";
import {
  useStartBucketReportJobMutation,
  useLazyGetBucketReportJobStatusQuery,
  useGetReportPdfMutation,
} from "features/trading/accounts";
import { BucketNumberEnum } from "features/trading/buckets";
import BucketSummarySelect from "components/buckets/BucketSummarySelect";
import { pollReportJob, reportJobErrorMessage } from "./reportJobPolling";
import {
  ReportSettingsProvider,
  useReportSettings,
} from "./ReportSettingsContext";
import TransactionModal from "./TransactionModal";
import RequestNotification from "components/ui/RequestNotification";
import { extractReportErrorMessage } from "./extractReportErrorMessage";

const STRATEGY_REPORT_DEFAULTS = {
  STARTING_VALUE: 10000000,
  ENDING_VALUE: 10450000,
  NET_CHANGE: 450000,
  TIME_WEIGHTED_RETURN: 4.5,
  INCOME_RECEIVED: 125000,
  MARGIN_INTEREST: 15000,
  SHARES_START: 100000,
  SHARES_END: 100000,
  VALUE_START: 5000000,
  VALUE_END: 5200000,
  DIVIDENDS: 45000,
  PARTICIPATION_RATE: 95,
  BASIS: 500000,
  TAX_SAVINGS: 1500000,
  MAX_STOCK_DROP: -15,
  OEA_DROP: -8,
  COLLAR_PL: 75000,
  AVAILABLE_MARGIN_BALANCE: 150000,
  OVERALL_DIVERSIFIED_PERFORMANCE: 4.3,
  TAX_LOSS_HARVEST_SLEEVE: 0.3,
  BEGINNING_CONCENTRATION: 85,
  CURRENT_CONCENTRATION: 20,
  HOLDINGS: 12,
  REALIZED_LOSSES: 150000,
  LOSSES_USED: 50000,
  POOL_REMAINING: 100000,
  UNREALIZED_GAINS: 250000,
  SHARES_SOLD: 5000,
  SELLABLE_TODAY: 15000,
  DRAWDOWN_CHART_LIMIT: 20,
  DRAWDOWN_CHART_NEGATIVE_LIMIT: -20,
};

const FULL_ALLOCATION_PCT = 100;

// The Protect card's per-asset max-drop dictionary from the report response.
type ProtectDrops = ProtectThemedCardValues["max_stock_drop"];

const DRAWDOWN_CHART_TITLE = "Drawdown Comparison";
const ALLOCATION_CHART_TITLE = "Current Allocation";
const CHART_SERIES_STOCK = "Stock";
const CHART_SERIES_OEA = "OEA";
const CHART_SERIES_STRATEGY = "Strategy";

function StrategyReportContent() {
  // SingleValueCard hooks
  const [startingValue, setStartingValue] = useStrategyReportValue(
    "starting_value_strategy",
    STRATEGY_REPORT_DEFAULTS.STARTING_VALUE,
  );
  const [endingValue, setEndingValue] = useStrategyReportValue(
    "ending_value_strategy",
    STRATEGY_REPORT_DEFAULTS.ENDING_VALUE,
  );
  const [netChange, setNetChange] = useStrategyReportValue(
    "net_change_strategy",
    STRATEGY_REPORT_DEFAULTS.NET_CHANGE,
  );
  const [timeWeightedReturn, setTimeWeightedReturn] = useStrategyReportValue(
    "time_weighted_return_strategy",
    STRATEGY_REPORT_DEFAULTS.TIME_WEIGHTED_RETURN,
  );
  const [incomeReceived, setIncomeReceived] = useStrategyReportValue(
    "income_received_strategy",
    STRATEGY_REPORT_DEFAULTS.INCOME_RECEIVED,
  );
  const [marginInterest, setMarginInterest] = useStrategyReportValue(
    "margin_interest_strategy",
    STRATEGY_REPORT_DEFAULTS.MARGIN_INTEREST,
  );

  // MultiValuesCard hooks - Keep
  const [sharesStart, setSharesStart] = useStrategyReportValue(
    "shares_start_strategy",
    STRATEGY_REPORT_DEFAULTS.SHARES_START,
  );
  const [sharesEnd, setSharesEnd] = useStrategyReportValue(
    "shares_end_strategy",
    STRATEGY_REPORT_DEFAULTS.SHARES_END,
  );
  const [valueStart, setValueStart] = useStrategyReportValue(
    "value_start_strategy",
    STRATEGY_REPORT_DEFAULTS.VALUE_START,
  );
  const [valueEnd, setValueEnd] = useStrategyReportValue(
    "value_end_strategy",
    STRATEGY_REPORT_DEFAULTS.VALUE_END,
  );
  const [dividends, setDividends] = useStrategyReportValue(
    "dividends_strategy",
    STRATEGY_REPORT_DEFAULTS.DIVIDENDS,
  );
  // The storage key keeps its old name so users' saved values survive.
  const [participationRate, setParticipationRate] = useStrategyReportValue(
    "upside_participation_strategy",
    STRATEGY_REPORT_DEFAULTS.PARTICIPATION_RATE,
  );
  const [basis, setBasis] = useStrategyReportValue(
    "basis_strategy",
    STRATEGY_REPORT_DEFAULTS.BASIS,
  );
  const [taxSavings, setTaxSavings] = useStrategyReportValue(
    "tax_savings_strategy",
    STRATEGY_REPORT_DEFAULTS.TAX_SAVINGS,
  );

  // MultiValuesCard hooks - Protect
  const [maxStockDrop, setMaxStockDrop] = useStrategyReportValue(
    "max_stock_drop_strategy",
    STRATEGY_REPORT_DEFAULTS.MAX_STOCK_DROP,
  );
  const [oeaDrop, setOeaDrop] = useStrategyReportValue(
    "oea_drop_strategy",
    STRATEGY_REPORT_DEFAULTS.OEA_DROP,
  );
  const [collarPL, setCollarPL] = useStrategyReportValue(
    "collar_pl_strategy",
    STRATEGY_REPORT_DEFAULTS.COLLAR_PL,
  );
  const [availableMarginBalance, setAvailableMarginBalance] =
    useStrategyReportValue(
      "available_margin_balance_strategy",
      STRATEGY_REPORT_DEFAULTS.AVAILABLE_MARGIN_BALANCE,
    );

  // MultiValuesCard hooks - Diversify
  const [overallDiversifiedPerformance, setOverallDiversifiedPerformance] =
    useStrategyReportValue(
      "overall_diversified_performance_strategy",
      STRATEGY_REPORT_DEFAULTS.OVERALL_DIVERSIFIED_PERFORMANCE,
    );
  const [taxLossHarvestSleeve, setTaxLossHarvestSleeve] =
    useStrategyReportValue(
      "tax_loss_harvest_sleeve_strategy",
      STRATEGY_REPORT_DEFAULTS.TAX_LOSS_HARVEST_SLEEVE,
    );
  const [beginningConcentration, setBeginningConcentration] =
    useStrategyReportValue(
      "beginning_concentration_strategy",
      STRATEGY_REPORT_DEFAULTS.BEGINNING_CONCENTRATION,
    );
  const [currentConcentration, setCurrentConcentration] =
    useStrategyReportValue(
      "current_concentration_strategy",
      STRATEGY_REPORT_DEFAULTS.CURRENT_CONCENTRATION,
    );
  const [holdings, setHoldings] = useStrategyReportValue(
    "holdings_strategy",
    STRATEGY_REPORT_DEFAULTS.HOLDINGS,
  );

  // MultiValuesCard hooks - Harvest Tax Benefits
  const [realizedLosses, setRealizedLosses] = useStrategyReportValue(
    "realized_losses_strategy",
    STRATEGY_REPORT_DEFAULTS.REALIZED_LOSSES,
  );
  const [lossesUsed, setLossesUsed] = useStrategyReportValue(
    "losses_used_strategy",
    STRATEGY_REPORT_DEFAULTS.LOSSES_USED,
  );
  const [poolRemaining, setPoolRemaining] = useStrategyReportValue(
    "pool_remaining_strategy",
    STRATEGY_REPORT_DEFAULTS.POOL_REMAINING,
  );
  const [unrealizedGains, setUnrealizedGains] = useStrategyReportValue(
    "unrealized_gains_strategy",
    STRATEGY_REPORT_DEFAULTS.UNREALIZED_GAINS,
  );
  const [sharesSold, setSharesSold] = useStrategyReportValue(
    "shares_sold_strategy",
    STRATEGY_REPORT_DEFAULTS.SHARES_SOLD,
  );
  const [sellableToday, setSellableToday] = useStrategyReportValue(
    "sellable_today_strategy",
    STRATEGY_REPORT_DEFAULTS.SELLABLE_TODAY,
  );

  // The Keep card's values: the response carries one entry per Bucket 1
  // asset plus their total, and the option picked in the select next to
  // Shares End drives every field of the card.
  const [keepShares, setKeepShares] = useState<KeepThemedCardValues | null>(
    null,
  );
  const [selectedKeepAsset, setSelectedKeepAsset] = useState(KEEP_TOTAL_OPTION);
  const [taxSettingsAnchor, setTaxSettingsAnchor] =
    useState<HTMLElement | null>(null);

  const applyKeepShares = useCallback(
    (card: KeepThemedCardValues, option: string) => {
      const values =
        option === KEEP_TOTAL_OPTION ? card.total : card.assets[option];
      setSharesStart(values.shares_start);
      setSharesEnd(values.shares_end);
      setValueStart(values.value_start);
      setValueEnd(values.value_end);
      setDividends(values.dividends);
      setBasis(values.basis);
      setTaxSavings(values.tax_savings);
    },
    [
      setSharesStart,
      setSharesEnd,
      setValueStart,
      setValueEnd,
      setDividends,
      setBasis,
      setTaxSavings,
    ],
  );

  const handleKeepAssetChange = useCallback(
    (option: string) => {
      setSelectedKeepAsset(option);
      if (keepShares) applyKeepShares(keepShares, option);
    },
    [keepShares, applyKeepShares],
  );

  // The Protect card's per-asset max stock drop: the response returns a
  // dictionary keyed by symbol, and the asset picked in the select next
  // to Max Stock Drop drives which asset's drop the field shows.
  const [protectDrops, setProtectDrops] = useState<ProtectDrops | null>(null);
  const [selectedProtectAsset, setSelectedProtectAsset] = useState("");

  const applyProtectDrop = useCallback(
    (drops: ProtectDrops, symbol: string) => {
      setMaxStockDrop(drops[symbol] ?? 0);
    },
    [setMaxStockDrop],
  );

  const handleProtectAssetChange = useCallback(
    (symbol: string) => {
      setSelectedProtectAsset(symbol);
      if (protectDrops) applyProtectDrop(protectDrops, symbol);
    },
    [protectDrops, applyProtectDrop],
  );

  // The calculated themed-card values → the Keep/Protect cards. The
  // backend's reserved-for-now zeros (participation_rate,
  // available_margin_balance) are deliberately NOT applied, so those
  // fields keep their stored values until the real calculations land.
  // The per-asset dictionaries (keep values, max_stock_drop) are applied
  // by their own dropdown flows; oea_drop is null when no bucket has
  // daily values — the stored value stays in that case too.
  const applyThemedCards = useCallback(
    (themedCards: BucketThemedCards) => {
      const { keep, protect } = themedCards;
      const card = keep?.assets ? keep : null;
      setKeepShares(card);
      if (card) {
        // Keep the previously picked option when the new results still
        // carry it; otherwise fall back to the total.
        const option = card.assets[selectedKeepAsset]
          ? selectedKeepAsset
          : KEEP_TOTAL_OPTION;
        setSelectedKeepAsset(option);
        applyKeepShares(card, option);
      }
      if (protect) {
        if (protect.oea_drop != null) {
          setOeaDrop(protect.oea_drop);
        }
        setCollarPL(protect.collar_pl);
      }
    },
    [selectedKeepAsset, applyKeepShares, setOeaDrop, setCollarPL],
  );

  // Update keepValues with hooks
  const keepValuesWithHooks: ReportValueCardProps[] = [
    {
      label: StrategyReportThemedCardValuesLabels.SHARES_START,
      value: sharesStart,
      type: SingleValueCardType.NUMBER,
      onValueChange: setSharesStart,
    },
    {
      label: StrategyReportThemedCardValuesLabels.SHARES_END,
      value: sharesEnd,
      type: SingleValueCardType.NUMBER,
      onValueChange: setSharesEnd,
    },
    {
      label: StrategyReportThemedCardValuesLabels.VALUE_START,
      value: valueStart,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setValueStart,
    },
    {
      label: StrategyReportThemedCardValuesLabels.VALUE_END,
      value: valueEnd,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setValueEnd,
    },
    {
      label: StrategyReportThemedCardValuesLabels.DIVIDENDS,
      value: dividends,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setDividends,
    },
    {
      label: StrategyReportThemedCardValuesLabels.PARTICIPATION_RATE,
      value: participationRate,
      type: SingleValueCardType.PERCENTAGE,
      onValueChange: setParticipationRate,
    },
    {
      label: StrategyReportThemedCardValuesLabels.BASIS,
      value: basis,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setBasis,
    },
    {
      label: StrategyReportThemedCardValuesLabels.TAX_SAVINGS,
      value: taxSavings,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setTaxSavings,
      labelAction: (
        <>
          <IconButton
            size="small"
            aria-label="Tax savings settings"
            sx={{ p: 0.25, fontSize: 16 }}
            onClick={(event) => setTaxSettingsAnchor(event.currentTarget)}
          >
            <SettingsIcon fontSize="inherit" />
          </IconButton>
          <Popover
            open={Boolean(taxSettingsAnchor)}
            anchorEl={taxSettingsAnchor}
            onClose={() => setTaxSettingsAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          >
            <Box sx={{ p: 2, width: 320 }}>
              <StrategyTaxStateSetting />
              <StrategyFederalTaxSetting />
            </Box>
          </Popover>
        </>
      ),
    },
  ];

  // Update protectValues with hooks
  const protectValuesWithHooks: ReportValueCardProps[] = [
    {
      label: StrategyReportThemedCardValuesLabels.MAX_STOCK_DROP,
      value: maxStockDrop,
      type: SingleValueCardType.PERCENTAGE,
      onValueChange: setMaxStockDrop,
    },
    {
      label: StrategyReportThemedCardValuesLabels.OEA_DROP,
      value: oeaDrop,
      type: SingleValueCardType.PERCENTAGE,
      onValueChange: setOeaDrop,
    },
    {
      label: StrategyReportThemedCardValuesLabels.COLLAR_PL,
      value: collarPL,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setCollarPL,
    },
    {
      label: StrategyReportThemedCardValuesLabels.AVAILABLE_MARGIN_BALANCE,
      value: availableMarginBalance,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setAvailableMarginBalance,
    },
  ];

  // Update diversifyValues with hooks
  const diversifyValuesWithHooks: ReportValueCardProps[] = [
    {
      label: StrategyReportThemedCardValuesLabels.OVERALL_DIVERSIFIED_PERFORMANCE,
      value: overallDiversifiedPerformance,
      type: SingleValueCardType.PERCENTAGE,
      onValueChange: setOverallDiversifiedPerformance,
    },
    {
      label: StrategyReportThemedCardValuesLabels.TAX_LOSS_HARVEST_SLEEVE,
      value: taxLossHarvestSleeve,
      type: SingleValueCardType.PERCENTAGE,
      onValueChange: setTaxLossHarvestSleeve,
    },
    {
      label: StrategyReportThemedCardValuesLabels.BEGINNING_CONCENTRATION,
      value: beginningConcentration,
      type: SingleValueCardType.PERCENTAGE,
      onValueChange: setBeginningConcentration,
    },
    {
      label: StrategyReportThemedCardValuesLabels.CURRENT_CONCENTRATION,
      value: currentConcentration,
      type: SingleValueCardType.PERCENTAGE,
      onValueChange: setCurrentConcentration,
    },
    {
      label: StrategyReportThemedCardValuesLabels.HOLDINGS,
      value: holdings,
      type: SingleValueCardType.NUMBER,
      onValueChange: setHoldings,
    },
  ];

  // Update harvestTaxBenefitsValues with hooks
  const harvestTaxBenefitsValuesWithHooks: ReportValueCardProps[] = [
    {
      label: StrategyReportThemedCardValuesLabels.REALIZED_LOSSES,
      value: realizedLosses,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setRealizedLosses,
    },
    {
      label: StrategyReportThemedCardValuesLabels.LOSSES_USED,
      value: lossesUsed,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setLossesUsed,
    },
    {
      label: StrategyReportThemedCardValuesLabels.POOL_REMAINING,
      value: poolRemaining,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setPoolRemaining,
    },
    {
      label: StrategyReportThemedCardValuesLabels.UNREALIZED_GAINS,
      value: unrealizedGains,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setUnrealizedGains,
    },
    {
      label: StrategyReportThemedCardValuesLabels.SHARES_SOLD,
      value: sharesSold,
      type: SingleValueCardType.NUMBER,
      onValueChange: setSharesSold,
    },
    {
      label: StrategyReportThemedCardValuesLabels.SELLABLE_TODAY,
      value: sellableToday,
      type: SingleValueCardType.NUMBER,
      onValueChange: setSellableToday,
    },
  ];

  const [chartDataPoints, setChartDataPoints] = useState<
    ReportChartDataPoint[]
  >([]);
  const [drawdownChartLimit, setDrawdownChartLimit] = useStrategyReportValue(
    "drawdown_chart_limit_strategy",
    STRATEGY_REPORT_DEFAULTS.DRAWDOWN_CHART_LIMIT,
  );
  const [drawdownChartNegativeLimit, setDrawdownChartNegativeLimit] =
    useStrategyReportValue(
      "drawdown_chart_negative_limit_strategy",
      STRATEGY_REPORT_DEFAULTS.DRAWDOWN_CHART_NEGATIVE_LIMIT,
    );
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  // The bucket report intentionally has no exclude_transactions yet (batch
  // contract kept minimal until the aggregation phase) — deselection is
  // collected here so the modal keeps working, but it doesn't affect the
  // calculation for now.
  const [deselectedTransactions, setDeselectedTransactions] = useState<
    OrionTransaction[]
  >([]);
  const [getReportPdf, { isLoading: isPdfLoading }] = useGetReportPdfMutation();
  const { settingsInfo } = useReportSettings();
  // Both rates are edited in the Keep card's tax settings and sent with
  // Calculate, where they drive that card's tax savings.
  const [federalTaxRate] = useFederalTaxRate();
  const { stateTaxRate } = useStateTaxRate(settingsInfo.client_id ?? null);
  // Strategy reports run over the account's persisted Bucket 1/2 — no
  // Strategy selection involved. This phase produces the combined excel
  // (one sheet per pair) + per-pair statuses; the cards/charts keep their
  // stored values until bucket aggregation lands.
  const [startBucketReportJob] = useStartBucketReportJobMutation();
  const [fetchBucketJobStatus] = useLazyGetBucketReportJobStatusQuery();
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationError, setCalculationError] = useState("");
  const [pairFailures, setPairFailures] = useState<BucketPairReportStatus[]>(
    [],
  );
  const [excelUrl, setExcelUrl] = useState<string | null>(null);
  const [bucketTotals, setBucketTotals] = useState<BucketTotals | null>(null);
  const [selectedSummary, setSelectedSummary] = useState<BucketSummaryEnum>(
    BucketSummaryEnum.BUCKET_1,
  );

  const applyBucketCards = useCallback(
    (totals: BucketTotals, summary: BucketSummaryEnum) => {
      const cards = totals[summary]?.single_info_cards;
      if (!cards) return;
      setStartingValue(cards.card_1);
      setEndingValue(cards.card_2);
      setNetChange(cards.card_3);
      setTimeWeightedReturn(cards.card_4);
      setIncomeReceived(cards.card_5);
      setMarginInterest(cards.card_6);
    },
    [
      setStartingValue,
      setEndingValue,
      setNetChange,
      setTimeWeightedReturn,
      setIncomeReceived,
      setMarginInterest,
    ],
  );

  const handleSummaryChange = useCallback(
    (summary: BucketSummaryEnum) => {
      setSelectedSummary(summary);
      if (bucketTotals) applyBucketCards(bucketTotals, summary);
    },
    [bucketTotals, applyBucketCards],
  );

  const themedCardsConfig = useMemo<ReportThemedCardConfig[]>(
    () => [
      {
        label: StrategyReportThemedCardLabels.KEEP,
        description: StrategyReportThemedCardDescriptions.KEEP,
        values: keepValuesWithHooks,
        addictional_component: null,
        headerAction: keepShares ? (
          <Select
            size="small"
            value={selectedKeepAsset}
            onChange={(event) => handleKeepAssetChange(event.target.value)}
            sx={{ bgcolor: "background.paper" }}
          >
            {[KEEP_TOTAL_OPTION, ...Object.keys(keepShares.assets)].map(
              (option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ),
            )}
          </Select>
        ) : undefined,
      },
      {
        label: StrategyReportThemedCardLabels.PROTECT,
        description: StrategyReportThemedCardDescriptions.PROTECT,
        values: protectValuesWithHooks,
        // Which asset's max_stock_drop the card (and its drawdown chart)
        // shows.
        headerAction: protectDrops ? (
          <Select
            size="small"
            value={selectedProtectAsset}
            onChange={(event) => handleProtectAssetChange(event.target.value)}
            sx={{ bgcolor: "background.paper" }}
          >
            {Object.keys(protectDrops).map((symbol) => (
              <MenuItem key={symbol} value={symbol}>
                {symbol}
              </MenuItem>
            ))}
          </Select>
        ) : undefined,
        addictional_component: {
          type: PDFReportChartType.BAR,
          title: DRAWDOWN_CHART_TITLE,
          limit: drawdownChartLimit as number,
          negativeLimit: drawdownChartNegativeLimit as number,
          data: [
            {
              label: CHART_SERIES_STOCK,
              value:
                typeof maxStockDrop === "number"
                  ? maxStockDrop
                  : parseFloat(String(maxStockDrop)) || 0,
              type: PDFReportValueType.PERCENTAGE,
            },
            {
              label: CHART_SERIES_OEA,
              value:
                typeof oeaDrop === "number"
                  ? oeaDrop
                  : parseFloat(String(oeaDrop)) || 0,
              type: PDFReportValueType.PERCENTAGE,
            },
          ],
        },
      },
      {
        label: StrategyReportThemedCardLabels.DIVERSIFY,
        description: StrategyReportThemedCardDescriptions.DIVERSIFY,
        values: diversifyValuesWithHooks,
        addictional_component: {
          type: PDFReportChartType.DONUT,
          title: ALLOCATION_CHART_TITLE,
          limit: FULL_ALLOCATION_PCT,
          data: [
            {
              label: CHART_SERIES_STOCK,
              value: currentConcentration as number,
              type: PDFReportValueType.PERCENTAGE,
            },
            {
              label: CHART_SERIES_STRATEGY,
              value: FULL_ALLOCATION_PCT - (currentConcentration as number),
              type: PDFReportValueType.PERCENTAGE,
            },
          ],
        },
      },
      {
        label: StrategyReportThemedCardLabels.HARVEST,
        description: StrategyReportThemedCardDescriptions.HARVEST,
        values: harvestTaxBenefitsValuesWithHooks,
        addictional_component: null,
      },
    ],
    [
      keepValuesWithHooks,
      protectValuesWithHooks,
      diversifyValuesWithHooks,
      harvestTaxBenefitsValuesWithHooks,
      drawdownChartLimit,
      drawdownChartNegativeLimit,
      maxStockDrop,
      oeaDrop,
      currentConcentration,
      keepShares,
      selectedKeepAsset,
      handleKeepAssetChange,
      protectDrops,
      selectedProtectAsset,
      handleProtectAssetChange,
    ],
  );

  const buildPdfRequest = useCallback((): PDFReportRequest => {
    const chart_data: ReportChartDataPoint[] = chartDataPoints.map((p) => ({
      date: String(p.date),
      option_total: Number(p.option_total),
      stock_total: Number(p.stock_total),
    }));

    const client_info: PDFReportClientInfoItem[] = [
      {
        label: StrategyReportSingleValueCardLabels.STARTING_VALUE,
        value: String(startingValue),
        type: PDFReportValueType.CURRENCY,
        description: StrategyReportSingleValueCardDescriptions.STARTING_VALUE,
      },
      {
        label: StrategyReportSingleValueCardLabels.ENDING_VALUE_PDF,
        value: String(endingValue),
        type: PDFReportValueType.CURRENCY,
        description: StrategyReportSingleValueCardDescriptions.ENDING_VALUE,
      },
      {
        label: StrategyReportSingleValueCardLabels.NET_CHANGE,
        value: String(netChange),
        type: PDFReportValueType.CURRENCY,
        description: StrategyReportSingleValueCardDescriptions.NET_CHANGE,
      },
      {
        label: StrategyReportSingleValueCardLabels.TIME_WEIGHTED_RETURN_PDF,
        value: String(timeWeightedReturn),
        type: PDFReportValueType.PERCENTAGE,
        description:
          StrategyReportSingleValueCardDescriptions.TIME_WEIGHTED_RETURN,
      },
      {
        label: StrategyReportSingleValueCardLabels.INCOME_RECEIVED,
        value: String(incomeReceived),
        type: PDFReportValueType.CURRENCY,
        description: StrategyReportSingleValueCardDescriptions.INCOME_RECEIVED,
      },
      {
        label: StrategyReportSingleValueCardLabels.MARGIN_INTEREST,
        value: String(marginInterest),
        type: PDFReportValueType.CURRENCY,
        description: StrategyReportSingleValueCardDescriptions.MARGIN_INTEREST,
      },
    ];

    const theme_info: PDFReportThemeInfoItem[] = themedCardsConfig.map(
      (card) => ({
        label: card.label,
        description: card.description,
        values: toPdfValues(card.values),
        addictional_component: card.addictional_component,
      }),
    );

    return {
      type: "strategy",
      settings_info: settingsInfo,
      chart_data,
      client_info,
      theme_info,
    };
  }, [
    chartDataPoints,
    themedCardsConfig,
    settingsInfo,
    startingValue,
    endingValue,
    netChange,
    timeWeightedReturn,
    incomeReceived,
    marginInterest,
  ]);

  const handleDownloadPdf = useCallback(async () => {
    try {
      const response: ReportPdfResponse =
        await getReportPdf(buildPdfRequest()).unwrap();
      if (response.download_url) {
        const a = document.createElement("a");
        a.href = response.download_url;
        a.click();
        a.remove();
        return;
      }
      throw new Error("Failed to download PDF");
    } catch (error) {
      throw new Error("Failed to download PDF");
    }
  }, [getReportPdf, buildPdfRequest]);

  const openTransactionModal = useCallback(() => {
    setIsTransactionModalOpen(true);
  }, []);

  const closeTransactionModal = useCallback(() => {
    setIsTransactionModalOpen(false);
  }, []);

  const handleCalculate = useCallback(async () => {
    const [start_date, end_date] = settingsInfo.period;

    if (!settingsInfo.orion_account_id || !start_date || !end_date) {
      setCalculationError(
        "Cannot calculate report: missing account or date range.",
      );
      return;
    }

    setPairFailures([]);
    setIsCalculating(true);
    try {
      // Buckets are CP-only: the backend runs one combined call+put report
      // per pair, so no option_type is sent. Bucket 1/2 belong to the page's
      // account; the selected Bucket 3 is passed by ID (it may belong to a
      // different account of the client) and adds one account-level sheet
      // with dynamically calculated values.
      const started = await startBucketReportJob({
        orion_account_id: settingsInfo.orion_account_id,
        start_date,
        end_date,
        buckets: [BucketNumberEnum.BUCKET_1, BucketNumberEnum.BUCKET_2],
        ...(settingsInfo.bucket_3_id
          ? { bucket_3_id: settingsInfo.bucket_3_id }
          : {}),
        federal_tax_rate: federalTaxRate,
        ...(stateTaxRate != null ? { state_tax_rate: stateTaxRate } : {}),
      }).unwrap();
      const job = await pollReportJob(started, (key) =>
        fetchBucketJobStatus({ key }).unwrap(),
      );
      if (job.status !== ReportJobStatus.COMPLETED || !job.result) {
        setCalculationError(
          reportJobErrorMessage(
            job,
            "Failed to calculate strategy report. Please try again.",
          ),
        );
        return;
      }
      setExcelUrl(job.result.excel_url);
      // Pairs without option history still succeed (portfolio-only sheet)
      // — only real errors pop up here.
      setPairFailures(
        job.result.reports.filter(
          (report) => report.status === BucketReportStatusEnum.ERROR,
        ),
      );

      const totals = job.result.bucket_totals ?? {};
      setBucketTotals(totals);
      // A summary with no successful data is absent from totals.
      const summaryToShow = totals[selectedSummary]
        ? selectedSummary
        : Object.values(BucketSummaryEnum).find((summary) => totals[summary]);
      if (summaryToShow) {
        setSelectedSummary(summaryToShow);
        applyBucketCards(totals, summaryToShow);
      }

      applyThemedCards(job.result.themed_cards ?? {});

      const protect = job.result.themed_cards?.protect;
      const drops = protect?.max_stock_drop ?? {};
      const dropSymbols = Object.keys(drops);
      setProtectDrops(dropSymbols.length ? drops : null);
      if (dropSymbols.length) {
        // Keep the previously picked asset when the new results still
        // carry it; otherwise default to the bucket's main asset, then
        // the first one.
        const assetToShow = dropSymbols.includes(selectedProtectAsset)
          ? selectedProtectAsset
          : (protect?.main_symbol ?? dropSymbols[0]);
        setSelectedProtectAsset(assetToShow);
        applyProtectDrop(drops, assetToShow);
      }

      // Themed-cards / graph application from the old single-pair strategy
      // report — kept commented until the bucket report returns them too:
      //
      // const themedCards = data.themed_cards as
      //   | NewReportStrategyThemedCards
      //   | undefined;
      // if (themedCards && "keep" in themedCards) {
      //   setSharesStart(themedCards.keep.shares_start ?? (sharesStart as number));
      //   setSharesEnd(themedCards.keep.shares_end ?? (sharesEnd as number));
      //   setValueStart(themedCards.keep.value_start ?? (valueStart as number));
      //   setValueEnd(themedCards.keep.value_end ?? (valueEnd as number));
      //   setDividends(themedCards.keep.dividends ?? (dividends as number));
      //   setUpsideParticipation(
      //     themedCards.keep.upside_participation ?? (upsideParticipation as number),
      //   );
      //   setBasis(themedCards.keep.basis ?? (basis as number));
      //   setTaxSavings(themedCards.keep.tax_savings ?? (taxSavings as number));
      //
      //   setMaxStockDrop(
      //     themedCards.protect.max_stock_drop ?? (maxStockDrop as number),
      //   );
      //   setOeaDrop(themedCards.protect.oea_drop ?? (oeaDrop as number));
      //   setCollarPL(themedCards.protect.collar_pl ?? (collarPL as number));
      //   setAvailableMarginBalance(
      //     themedCards.protect.available_margin_balance ??
      //       (availableMarginBalance as number),
      //   );
      //
      //   setOverallDiversifiedPerformance(
      //     themedCards.diversify.overall_diversified_performance ??
      //       (overallDiversifiedPerformance as number),
      //   );
      //   setTaxLossHarvestSleeve(
      //     themedCards.diversify.tax_loss_harvest_sleeve ??
      //       (taxLossHarvestSleeve as number),
      //   );
      //   setBeginningConcentration(
      //     themedCards.diversify.beginning_concentration ??
      //       (beginningConcentration as number),
      //   );
      //   setCurrentConcentration(
      //     themedCards.diversify.current_concentration ??
      //       (currentConcentration as number),
      //   );
      //   setHoldings(themedCards.diversify.holdings ?? (holdings as number));
      //
      //   setRealizedLosses(
      //     themedCards.harvest.realized_losses ?? (realizedLosses as number),
      //   );
      //   setLossesUsed(themedCards.harvest.losses_used ?? (lossesUsed as number));
      //   setPoolRemaining(
      //     themedCards.harvest.pool_remaining ?? (poolRemaining as number),
      //   );
      //   setUnrealizedGains(
      //     themedCards.harvest.unrealized_gains ?? (unrealizedGains as number),
      //   );
      //   setSharesSold(themedCards.harvest.shares_sold ?? (sharesSold as number));
      //   setSellableToday(
      //     themedCards.harvest.sellable_today ?? (sellableToday as number),
      //   );
      // }
      //
      // setChartDataPoints(data.graph_or_table_data || []);
    } catch (e) {
      setCalculationError(
        extractReportErrorMessage(
          e,
          "Failed to calculate strategy report. Please try again.",
        ),
      );
    } finally {
      setIsCalculating(false);
    }
  }, [
    settingsInfo.period,
    settingsInfo.orion_account_id,
    settingsInfo.bucket_3_id,
    stateTaxRate,
    federalTaxRate,
    startBucketReportJob,
    fetchBucketJobStatus,
    setCalculationError,
    selectedSummary,
    applyBucketCards,
    selectedProtectAsset,
    applyProtectDrop,
    applyThemedCards,
  ]);

  const handleDownloadExcel = useCallback(() => {
    if (!excelUrl) return;
    const a = document.createElement("a");
    a.href = excelUrl;
    a.click();
    a.remove();
  }, [excelUrl]);

  // Reset job outputs when key report inputs change
  useEffect(() => {
    setExcelUrl(null);
    setPairFailures([]);
    setBucketTotals(null);
    setKeepShares(null);
    setProtectDrops(null);
  }, [
    settingsInfo.orion_account_id,
    settingsInfo.period?.[0],
    settingsInfo.period?.[1],
    // A new rate means a new job — the ready excel belongs to the old one.
    stateTaxRate,
    federalTaxRate,
  ]);

  return (
    <Card>
      <CardContent>
        <RequestNotification
          open={Boolean(calculationError)}
          title="Calculation failed"
          message={calculationError}
          onClose={() => setCalculationError("")}
        />
        <RequestNotification
          open={pairFailures.length > 0}
          severity="warning"
          title={`${pairFailures.length} bucket report(s) failed`}
          message={pairFailures
            .map(
              (failure) =>
                `B${failure.bucket}${
                  failure.symbol ? ` ${failure.symbol}` : ""
                }${
                  failure.option_type ? ` ${failure.option_type}` : ""
                }: ${failure.message}`,
            )
            .join(" · ")}
          onClose={() => setPairFailures([])}
        />
        <SettingsReport
          type={PerformanceReportTypeEnum.STRATEGY}
          header="Optic Equity Access Performance"
          subtitle="OPTIC ASSET MANAGEMENT"
        />
        <Grid container justifyContent="center" sx={{ mt: 1 }}>
          <Grid item sx={{ mr: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleDownloadPdf}
              disabled={isPdfLoading}
            >
              {isPdfLoading ? "Generating…" : "Download PDF"}
            </Button>
          </Grid>
          <Grid item sx={{ mr: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleDownloadExcel}
              disabled={!excelUrl}
            >
              {excelUrl ? "Download Excel" : "Download Excel"}
            </Button>
          </Grid>
          <Grid item sx={{ mr: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCalculate}
              disabled={isPdfLoading || isCalculating}
            >
              {isCalculating ? "Calculating…" : `Calculate`}
            </Button>
          </Grid>
          <Grid item sx={{ mr: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={openTransactionModal}
              disabled={isPdfLoading}
            >
              {isPdfLoading ? "Updating…" : "Transactions"}
            </Button>
          </Grid>
        </Grid>
        <ChartsReport
          chartDataPoints={chartDataPoints}
          onChartDataChange={setChartDataPoints}
        />
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Typography variant="h6">Summary</Typography>
          <BucketSummarySelect
            value={selectedSummary}
            bucketTotals={bucketTotals ?? undefined}
            onChange={handleSummaryChange}
          />
        </Grid>
        <Grid container spacing={4} columns={{ lg: 12, md: 6, xs: 12 }}>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={startingValue as number}
              label={StrategyReportSingleValueCardLabels.STARTING_VALUE}
              description={
                StrategyReportSingleValueCardDescriptions.STARTING_VALUE
              }
              type={SingleValueCardType.CURRENCY}
              color={SingleValueCardColor.PRIMARY}
              onValueChange={setStartingValue}
            />
          </Grid>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={endingValue as number}
              label={StrategyReportSingleValueCardLabels.ENDING_VALUE_CARD}
              description={StrategyReportSingleValueCardDescriptions.ENDING_VALUE}
              type={SingleValueCardType.CURRENCY}
              color={SingleValueCardColor.PRIMARY}
              onValueChange={setEndingValue}
            />
          </Grid>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={netChange as number}
              label={StrategyReportSingleValueCardLabels.NET_CHANGE}
              description={StrategyReportSingleValueCardDescriptions.NET_CHANGE}
              type={SingleValueCardType.CURRENCY}
              color={SingleValueCardColor.PRIMARY}
              onValueChange={setNetChange}
            />
          </Grid>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={timeWeightedReturn as number}
              label={StrategyReportSingleValueCardLabels.TIME_WEIGHTED_RETURN_CARD}
              description={
                StrategyReportSingleValueCardDescriptions.TIME_WEIGHTED_RETURN
              }
              type={SingleValueCardType.PERCENTAGE}
              color={SingleValueCardColor.PRIMARY}
              onValueChange={setTimeWeightedReturn}
            />
          </Grid>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={incomeReceived as number}
              label={StrategyReportSingleValueCardLabels.INCOME_RECEIVED}
              description={
                StrategyReportSingleValueCardDescriptions.INCOME_RECEIVED
              }
              type={SingleValueCardType.CURRENCY}
              color={SingleValueCardColor.PRIMARY}
              onValueChange={setIncomeReceived}
            />
          </Grid>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={marginInterest as number}
              label={StrategyReportSingleValueCardLabels.MARGIN_INTEREST}
              description={
                StrategyReportSingleValueCardDescriptions.MARGIN_INTEREST
              }
              type={SingleValueCardType.CURRENCY}
              color={SingleValueCardColor.ERROR}
              onValueChange={setMarginInterest}
            />
          </Grid>
        </Grid>
        <Grid container spacing={4} columns={{ lg: 12, md: 6, xs: 12 }}>
          {themedCardsConfig.map((card, index) => (
            <Grid item lg={6} xs={12} md={6} key={card.label}>
              <MultiValuesCard
                title={card.label}
                values={card.values}
                headerAction={card.headerAction}
                description={
                  index === 0
                    ? StrategyReportThemedCardBlurbs.KEEP
                    : index === 1
                      ? StrategyReportThemedCardBlurbs.PROTECT
                      : index === 2
                        ? StrategyReportThemedCardBlurbs.DIVERSIFY
                        : StrategyReportThemedCardBlurbs.HARVEST
                }
                additionalComponents={
                  index === 1 ? (
                    <DrawdownComparisonChart
                      values={protectValuesWithHooks}
                      limit={drawdownChartLimit as number}
                      negativeLimit={drawdownChartNegativeLimit as number}
                      onLimitChange={setDrawdownChartLimit}
                      onNegativeLimitChange={setDrawdownChartNegativeLimit}
                    />
                  ) : index === 2 ? (
                    <CurrentAllocationChartReport
                      value={currentConcentration as number}
                    />
                  ) : null
                }
              />
            </Grid>
          ))}
        </Grid>
        <TransactionModal
          open={isTransactionModalOpen}
          onClose={closeTransactionModal}
          settingsInfo={settingsInfo}
          reportType={PerformanceReportTypeEnum.STRATEGY}
          onDeselectedTransactionsChange={setDeselectedTransactions}
        />
      </CardContent>
    </Card>
  );
}

export const StrategyReport = () => (
  <ReportSettingsProvider>
    <StrategyReportContent />
  </ReportSettingsProvider>
);

export default StrategyReport;
